import React from 'react';
import PropTypes from 'prop-types';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import NativeTable from '../../../components/NativeTable/NativeTable';
import {
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Button
} from "reactstrap";

import { 
    getEmployeeSalesRequest, 
    clearEmployeeSales,
    sendEmployeeSalesReportEmailRequest,
    sendEmployeeSalesReportSmsRequest,
    getPayoutStatusRequest,
    getPayoutHistoryRequest,
    retryPayoutRequest,
    triggerPayoutRequest,
    retryFailedTransfersRequest,
    getLocalManualPayReason,
    isWorkPeriodMarkedPaidLocally,
} from '../../../actions/users';
import { formatTipPayoutStatusLabel, resolveManualPayReasonFromRow, resolveEffectiveTipPayoutStatus } from '../../../utils/tipPayoutStatus';
import { hasRefundReporting, reportingNets, salesAmounts } from '../../../utils/employeeSalesAmounts';
import {
    formatApiTime,
    formatApiDateShort,
    calendarDateFromApi,
} from '../../../utils/merchantLocalDateTime';
import { loadStoreDetails } from '../../../actions/settings';
import Loader from '../../../components/Loader';
import Widget from '../../../components/Widget';
import { toast } from 'react-toastify';
import { withTranslation } from '../../../i18n/LanguageContext';
import s from './EmployeeSales.module.scss';

class EmployeeSales extends React.Component {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
    };

    state = {
        startDate: this.getDefaultStartDate(),
        endDate: this.getDefaultEndDate(),
        exportLoading: false,
        exportMenuOpen: false,
        sendReportLoading: null,
    };

    getDefaultStartDate() {
        const date = new Date();
        date.setDate(date.getDate() - 30); // Default to last 30 days
        return date.toISOString().split('T')[0];
    }

    getDefaultEndDate() {
        return new Date().toISOString().split('T')[0];
    }

    componentDidMount() {
        const userId = parseInt(this.props.match.params.userId);
        this.loadSalesData(userId);
        this.props.dispatch(getPayoutStatusRequest(userId));
        const { storeSettingsMerchantId, merchant } = this.props;
        if (storeSettingsMerchantId !== (merchant && merchant.MerchantId)) {
            this.props.dispatch(loadStoreDetails());
        }
    }

    componentWillUnmount() {
        this.props.dispatch(clearEmployeeSales());
    }

    loadSalesData = (userId) => {
        const { startDate, endDate } = this.state;
        this.props.dispatch(getEmployeeSalesRequest(userId, startDate, endDate));
        this.props.dispatch(getPayoutHistoryRequest(userId, startDate, endDate));
    }

    handleStartDateChange = (e) => {
        this.setState({ startDate: e.target.value }, () => {
            const userId = parseInt(this.props.match.params.userId);
            this.loadSalesData(userId);
        });
    }

    handleEndDateChange = (e) => {
        this.setState({ endDate: e.target.value }, () => {
            const userId = parseInt(this.props.match.params.userId);
            this.loadSalesData(userId);
        });
    }

    // ClockIn/ClockOut are already merchant-local from the API (no TZ suffix).
    parseCalendarDate = (raw) => calendarDateFromApi(raw);

    getDisplayWorkperiodDate(row) {
        if (!row) return null;

        const clockIn = row.ClockIn ?? row.clockIn;
        if (clockIn) {
            const clockInDate = calendarDateFromApi(clockIn);
            if (clockInDate) return clockInDate;
        }

        return calendarDateFromApi(row.WorkperiodDate || row.workperiodDate);
    }

    formatWorkperiodDate = (cell, row) => {
        if (row) {
            const clockIn = row.ClockIn ?? row.clockIn;
            if (clockIn) return formatApiDateShort(clockIn, '');
            return formatApiDateShort(row.WorkperiodDate || row.workperiodDate || cell, '');
        }
        return formatApiDateShort(cell, '');
    }

    formatCurrency = (cell) => {
        if (cell === null || cell === undefined) return '$0.00';
        return `$${parseFloat(cell).toFixed(2)}`;
    }

    formatHours = (cell) => {
        if (cell === null || cell === undefined) return '0.0';
        return parseFloat(cell).toFixed(1);
    }

    formatTime = (cell) => formatApiTime(cell, '-');


    isClockOutEmpty = (value) => {
        if (value === null || value === undefined) return true;
        const trimmed = String(value).trim();
        return trimmed === '' || trimmed === '0001-01-01T00:00:00';
    }

    formatClockOutDisplay = (cell) => {
        if (this.isClockOutEmpty(cell)) {
            return <span style={{ color: '#2ecc71', fontWeight: 600 }}>{this.props.t('report.stillActive')}</span>;
        }
        return this.formatTime(cell);
    }

    formatClockOutLabel = (cell) => {
        if (this.isClockOutEmpty(cell)) return this.props.t('report.stillActive');
        return this.formatTime(cell);
    }

    formatPeriodRange = (startDate, endDate) => {
        if (!startDate || !endDate) return '';
        const start = this.parseCalendarDate(startDate);
        const end = this.parseCalendarDate(endDate);
        if (!start || !end) return `${startDate} – ${endDate}`;

        const opts = { month: 'short', day: 'numeric', year: 'numeric' };
        return `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', opts)}`;
    }

    readSummaryNumber = (source, ...keys) => {
        if (!source) return 0;
        for (let i = 0; i < keys.length; i += 1) {
            const value = source[keys[i]];
            if (value !== null && value !== undefined && value !== '') {
                return value;
            }
        }
        return 0;
    }

    normalizePeriodSummary = (source) => {
        if (!source) return null;

        const amounts = salesAmounts(source);
        const nets = reportingNets(source);
        const tips = this.readSummaryNumber(source, 'Tips', 'tips');
        const gratuity = this.readSummaryNumber(source, 'Gratuity', 'gratuity');

        return {
            shiftCount: this.readSummaryNumber(source, 'ShiftCount', 'shiftCount'),
            totalHours: this.readSummaryNumber(source, 'TotalHours', 'totalHours'),
            cashSales: amounts.cash,
            cardSales: amounts.card,
            memberSales: amounts.member,
            fees: amounts.fees,
            totalSales: amounts.total,
            tips,
            gratuity,
            totalEarnings: this.readSummaryNumber(source, 'TotalEarnings', 'totalEarnings') || (tips + gratuity),
            tipsOwed: this.readSummaryNumber(source, 'TipsOwed', 'tipsOwed'),
            tipsPaid: this.readSummaryNumber(source, 'TipsPaid', 'tipsPaid'),
            salesBeforeRefunds: nets.salesBeforeRefunds,
            discounts: nets.discounts,
            comps: nets.comps,
            merchRefunds: nets.merchRefunds,
            netSalesMerch: nets.netSalesMerch,
            customerRefunds: nets.customerRefunds,
            tipRefundedDisplay: nets.tipRefundedDisplay,
            netTipsDisplay: nets.netTipsDisplay,
            gratuityRefundedDisplay: nets.gratuityRefundedDisplay,
            netGratuityDisplay: nets.netGratuityDisplay,
            feeRefundedDisplay: nets.feeRefundedDisplay,
            netFeeDisplay: nets.netFeeDisplay,
            merchRefundEstimated: nets.merchRefundEstimated,
            tipGratRefundEstimated: nets.tipGratRefundEstimated,
        };
    }

    readFlag = (row, ...keys) => {
        if (!row) return false;
        for (let i = 0; i < keys.length; i += 1) {
            const value = row[keys[i]];
            if (value === true || value === 1 || value === 'true' || value === 'True') return true;
            if (value === false || value === 0 || value === 'false' || value === 'False') return false;
        }
        return false;
    }

    isPooledPeriod = (row) => this.readFlag(row, 'PooledPeriod', 'pooledPeriod')

    isPeriodEnded = (row) => {
        const value = row?.PeriodEnded ?? row?.periodEnded;
        if (value === undefined || value === null || value === '') return true;
        return value === true || value === 1 || value === 'true' || value === 'True';
    }

    rowYourShare = (row) => {
        if (!this.isPooledPeriod(row) || !this.readFlag(row, 'InPooledShare', 'inPooledShare')) return 0;
        return parseFloat(row.YourShare ?? row.yourShare) || 0;
    }

    isPooledSharePaid = (row) => {
        const userId = parseInt(this.props.match.params.userId, 10);
        const workPeriodId = this.getPeriodRowKey(row);
        if (workPeriodId && isWorkPeriodMarkedPaidLocally(userId, workPeriodId)) return true;
        return this.readFlag(row, 'SharePaidToEmployee', 'sharePaidToEmployee');
    }

    pooledStatusLabel = (row) => {
        if (!this.readFlag(row, 'InPooledShare', 'inPooledShare')) return '—';
        return this.isPooledSharePaid(row) ? this.props.t('report.paid') : this.props.t('report.pending');
    }

    getPooledRowPaidOwed = (row) => {
        const share = this.rowYourShare(row);
        if (share <= 0) return { paid: 0, owed: 0 };
        if (!this.isPeriodEnded(row)) return { paid: 0, owed: 0 };
        if (this.isPooledSharePaid(row)) return { paid: share, owed: 0 };
        return { paid: 0, owed: share };
    }

    getRowTipPaidOwed = (row) => {
        if (this.isPooledPeriod(row)) {
            return this.getPooledRowPaidOwed(row);
        }
        const tips = parseFloat(row.Tips) || 0;
        const gratuity = parseFloat(row.Gratuity) || 0;
        const earnings = tips + gratuity;
        const status = (row.TipPayoutStatus || row.tipPayoutStatus || 'none').toLowerCase();
        const userId = parseInt(this.props.match.params.userId, 10);
        const workPeriodId = this.getPeriodRowKey(row);
        const isLocallyPaid = workPeriodId && isWorkPeriodMarkedPaidLocally(userId, workPeriodId);
        const effectiveStatus = resolveEffectiveTipPayoutStatus({
            apiStatus: status,
            isLocallyPaid,
            tipsOwedAmount: row.TipsOwedAmount ?? row.tipsOwedAmount,
            manualPayReason: this.resolveRowManualPayReason(row),
        });

        if (effectiveStatus === 'paid') {
            return { paid: earnings, owed: 0 };
        }

        if (effectiveStatus === 'pending_paid_to_manager') {
            return { paid: 0, owed: earnings };
        }

        if (effectiveStatus === 'partial') {
            const paidField = row.TipsPaidAmount ?? row.tipsPaidAmount;
            const owedField = row.TipsOwedAmount ?? row.tipsOwedAmount;
            if (paidField !== null && paidField !== undefined && owedField !== null && owedField !== undefined) {
                return {
                    paid: parseFloat(paidField) || 0,
                    owed: parseFloat(owedField) || 0,
                };
            }
        }

        const paidField = row.TipsPaidAmount ?? row.tipsPaidAmount;
        const owedField = row.TipsOwedAmount ?? row.tipsOwedAmount;

        if (paidField !== null && paidField !== undefined && owedField !== null && owedField !== undefined) {
            return {
                paid: parseFloat(paidField) || 0,
                owed: parseFloat(owedField) || 0,
            };
        }

        return { paid: 0, owed: earnings };
    }

    buildClientPeriodSummary = (rows, totals) => ({
        shiftCount: rows.length,
        totalHours: totals.hours,
        cashSales: totals.cash,
        cardSales: totals.card,
        memberSales: totals.member,
        fees: totals.fees,
        totalSales: totals.cash + totals.card + totals.member,
        tips: totals.tips,
        gratuity: totals.gratuity,
        totalEarnings: totals.tips + totals.gratuity,
        tipsOwed: totals.owed,
        tipsPaid: totals.paid,
        yourShare: totals.yourShare,
        includePooled: totals.includePooled,
        salesBeforeRefunds: totals.salesBeforeRefunds,
        discounts: totals.discounts,
        comps: totals.comps,
        merchRefunds: totals.merchRefunds,
        netSalesMerch: totals.netSalesMerch,
        customerRefunds: totals.customerRefunds,
        tipRefundedDisplay: totals.tipRefundedDisplay,
        netTipsDisplay: totals.netTipsDisplay,
        gratuityRefundedDisplay: totals.gratuityRefundedDisplay,
        netGratuityDisplay: totals.netGratuityDisplay,
        feeRefundedDisplay: totals.feeRefundedDisplay,
        netFeeDisplay: totals.netFeeDisplay,
        merchRefundEstimated: totals.merchRefundEstimated,
        tipGratRefundEstimated: totals.tipGratRefundEstimated,
    })

    getPeriodRowKey = (row) => {
        const directId = row.WorkPeriodId || row.workPeriodId || row.WorkperiodId || row.workperiodId;
        if (directId) {
            return String(directId);
        }

        const compositeId = row.id || row.Id;
        if (typeof compositeId === 'string' && compositeId.includes('-')) {
            return compositeId.split('-').pop();
        }

        const dateKey = row.WorkperiodDate || row.workperiodDate;
        return dateKey ? String(dateKey) : '';
    }

    filterRowsByDateRange = (rows, startDate, endDate) => {
        if (!startDate && !endDate) {
            return rows;
        }

        const start = startDate ? this.parseCalendarDate(startDate) : null;
        const end = endDate ? this.parseCalendarDate(endDate) : null;

        return rows.filter((row) => {
            const rowDate = this.getDisplayWorkperiodDate(row);
            if (!rowDate) {
                return false;
            }

            if (start && rowDate < start) {
                return false;
            }

            if (end && rowDate > end) {
                return false;
            }

            return true;
        });
    }

    resolvePeriodSummary = (serverSummary, rows, totals) => {
        const normalizedServerSummary = this.normalizePeriodSummary(serverSummary);
        const clientSummary = this.buildClientPeriodSummary(rows, totals);

        if (!normalizedServerSummary) {
            return clientSummary;
        }

        // Prefer server totals when they match the filtered row set; otherwise use client rollup.
        // Tip paid/owed always comes from visible rows so manual-pay status updates stay accurate.
        if (Number(normalizedServerSummary.shiftCount) === rows.length) {
            return {
                ...normalizedServerSummary,
                tipsPaid: clientSummary.tipsPaid,
                tipsOwed: clientSummary.tipsOwed,
                yourShare: clientSummary.yourShare,
                includePooled: clientSummary.includePooled,
            };
        }

        return clientSummary;
    }

    renderSummaryCard = (label, value, cardClassName = '', title = undefined) => (
        <div className={`${s.summaryCard}${cardClassName ? ` ${cardClassName}` : ''}`} title={title}>
            <span className={s.summaryLabel}>{label}</span>
            <span className={s.summaryValue}>{value}</span>
        </div>
    )

    renderPeriodSummary = (summary, startDate, endDate) => {
        if (!summary) return null;

        return (
            <div className={s.summaryPanel}>
                <p className={s.summaryPeriod}>
                    {summary.shiftCount > 0
                        ? this.props.t('report.summaryFor', { range: this.formatPeriodRange(startDate, endDate) })
                        : this.props.t('report.noShiftsIn', { range: this.formatPeriodRange(startDate, endDate) })}
                </p>

                <div className={s.summarySection}>
                    <h2 className={s.summarySectionTitle}>{this.props.t('report.overview')}</h2>
                    <div className={s.summaryCards}>
                        {this.renderSummaryCard(this.props.t('report.shifts'), summary.shiftCount)}
                        {this.renderSummaryCard(this.props.t('report.hours'), this.formatHours(summary.totalHours))}
                        {this.renderSummaryCard(this.props.t('report.salesBeforeRefunds'), this.formatCurrency(summary.totalSales))}
                        {summary.includePooled
                            ? this.renderSummaryCard(this.props.t('report.yourShare'), this.formatCurrency(summary.yourShare))
                            : null}
                        {summary.includePooled ? null : this.renderSummaryCard(this.props.t('report.tips'), this.formatCurrency(summary.tips))}
                        {summary.includePooled ? null : this.renderSummaryCard(this.props.t('report.gratuity'), this.formatCurrency(summary.gratuity))}
                        {summary.includePooled ? null : this.renderSummaryCard(this.props.t('report.earnings'), this.formatCurrency(summary.totalEarnings))}
                    </div>
                </div>

                <div className={s.summarySection}>
                    <h2 className={s.summarySectionTitle}>{this.props.t('report.salesBreakdown')}</h2>
                    <div className={s.summaryCards}>
                        {this.renderSummaryCard(this.props.t('report.cash'), this.formatCurrency(summary.cashSales))}
                        {this.renderSummaryCard(this.props.t('report.card'), this.formatCurrency(summary.cardSales))}
                        {this.renderSummaryCard(this.props.t('report.member'), this.formatCurrency(summary.memberSales))}
                    </div>
                </div>

                {hasRefundReporting(summary) ? (
                    <div className={s.summarySection}>
                        <h2 className={s.summarySectionTitle}>{this.props.t('report.afterRefunds')}</h2>
                        <div className={s.summaryCards}>
                            {summary.discounts > 0 ? this.renderSummaryCard(this.props.t('report.discounts'), this.formatCurrency(summary.discounts)) : null}
                            {summary.comps > 0 ? this.renderSummaryCard(this.props.t('report.comps'), this.formatCurrency(summary.comps)) : null}
                            {summary.merchRefunds > 0 ? this.renderSummaryCard(this.props.t('report.merchRefunds'), this.formatCurrency(summary.merchRefunds), '', this.props.t('report.merchRefundsHelp')) : null}
                            {summary.customerRefunds > 0 ? this.renderSummaryCard(this.props.t('report.customerRefunds'), this.formatCurrency(summary.customerRefunds), '', this.props.t('report.customerRefundsHelp')) : null}
                            {!summary.includePooled && summary.tipRefundedDisplay > 0
                                ? this.renderSummaryCard(this.props.t('report.netTips'), this.formatCurrency(summary.netTipsDisplay))
                                : null}
                            {!summary.includePooled && summary.gratuityRefundedDisplay > 0
                                ? this.renderSummaryCard(this.props.t('report.netGratuity'), this.formatCurrency(summary.netGratuityDisplay))
                                : null}
                            {summary.merchRefunds > 0 ? this.renderSummaryCard(this.props.t('report.salesAfterRefunds'), this.formatCurrency(summary.netSalesMerch ?? summary.totalSales)) : null}
                        </div>
                        <div className={s.reportExplainer}>
                            {summary.merchRefunds > 0 ? <p style={{ margin: '0 0 6px' }}>{this.props.t('report.merchRefundsHelp')}</p> : null}
                            {summary.customerRefunds > 0 ? <p style={{ margin: '0 0 6px' }}>{this.props.t('report.customerRefundsHelp')}</p> : null}
                            <p style={{ margin: 0 }}>
                                {this.props.t(summary.merchRefundEstimated || summary.tipGratRefundEstimated
                                    ? 'report.refundEstimateFootnote'
                                    : 'report.refundNetFootnote')}
                            </p>
                        </div>
                    </div>
                ) : null}

                <div className={s.summarySection}>
                    <h2 className={s.summarySectionTitle}>{this.props.t('report.tipPayouts')}</h2>
                    <div className={s.summaryCards}>
                        {this.renderSummaryCard(this.props.t('report.tipsOwed'), this.formatCurrency(summary.tipsOwed), s.owedCard)}
                        {this.renderSummaryCard(this.props.t('report.tipsPaid'), this.formatCurrency(summary.tipsPaid), s.paidCard)}
                    </div>
                </div>
            </div>
        );
    }

    // Earnings column formatter - shows Tips + Gratuity for each date
    earningsFormatter = (cell, row) => {
        const tips = parseFloat(row.Tips) || 0;
        const gratuity = parseFloat(row.Gratuity) || 0;
        const earnings = tips + gratuity;
        return this.formatCurrency(earnings);
    }

    resolveRowManualPayReason = (row) => {
        const userId = parseInt(this.props.match.params.userId, 10);
        const workPeriodId = this.getPeriodRowKey(row);
        const localReason = workPeriodId
            ? getLocalManualPayReason(userId, workPeriodId)
            : null;

        return resolveManualPayReasonFromRow(row, localReason || undefined);
    }

    // Mobile-visible status indicator
    statusFormatter = (cell, row) => {
        if (this.isPooledPeriod(row)) {
            if (!this.readFlag(row, 'InPooledShare', 'inPooledShare')) {
                return <span className={s.statusLabelDesktop}>—</span>;
            }
            const label = this.pooledStatusLabel(row);
            const paid = this.isPooledSharePaid(row);
            return (
                <span className={s.statusCell}>
                    <span className={s.statusDot + ' ' + (paid ? s.dotPaid : s.dotPending)} title={label}>{paid ? '\u2713' : '\u25cf'}</span>
                    <span className={s.statusLabelDesktop} title={label}>{label}</span>
                </span>
            );
        }
        const apiStatus = row.TipPayoutStatus || 'none';
        const userId = parseInt(this.props.match.params.userId, 10);
        const workPeriodId = this.getPeriodRowKey(row);
        const isLocallyPaid = workPeriodId && isWorkPeriodMarkedPaidLocally(userId, workPeriodId);
        const status = resolveEffectiveTipPayoutStatus({
            apiStatus,
            isLocallyPaid,
            tipsOwedAmount: row.TipsOwedAmount ?? row.tipsOwedAmount,
            manualPayReason: this.resolveRowManualPayReason(row),
        });
        const label = formatTipPayoutStatusLabel(status, this.resolveRowManualPayReason(row));
        const note = row.TipPayoutNote || row.tipPayoutNote || '';
        const title = note ? `${label} — ${note}` : label;

        if (status === 'paid') {
            return (
                <span className={s.statusCell}>
                    <span className={s.statusDot + ' ' + s.dotPaid} title={title}>&#x2713;</span>
                    <span className={s.statusLabelDesktop} title={title}>{label}</span>
                </span>
            );
        }
        if (status === 'pending_paid_to_manager') {
            return (
                <span className={s.statusCell}>
                    <span className={s.statusDot + ' ' + s.dotPending} title={title}>&#x25cf;</span>
                    <span className={s.statusLabelDesktop} title={title}>{label}</span>
                </span>
            );
        }
        if (status === 'failed') return <span className={s.statusDot + ' ' + s.dotFailed} title={title}>!</span>;
        if (status === 'partial') return <span className={s.statusDot + ' ' + s.dotPartial} title={title}>&#x25cb;</span>;
        if (status === 'pending') return <span className={s.statusDot + ' ' + s.dotPending} title={title}>&#x25cf;</span>;
        return null;
    }

    // Action column formatter with retry/trigger buttons
    actionFormatter = (cell, row) => {
        const { selectedEmployee, payoutStatus, retryingTransferId, triggeringSaleId, retryingFailedWorkPeriodId, payoutAttemptResultsByWorkPeriodId } = this.props;
        const hasStripeAccount = payoutStatus?.stripeAccountId || 
            (selectedEmployee?.StripeAccountId && selectedEmployee.StripeAccountId !== '');
        const isEligible = payoutStatus?.accountStatus === 'active';
        
        if (!hasStripeAccount || !isEligible) {
            return <span className={s.actionNa}>-</span>;
        }

        const status = row.TipPayoutStatus || 'none';
        const tips = parseFloat(row.Tips) || 0;
        const gratuity = parseFloat(row.Gratuity) || 0;
        const hasAmount = (tips + gratuity) > 0;
        const isRetrying = retryingTransferId === row.TransferId;
        const isTriggering = triggeringSaleId === row.id;
        const workPeriodId = row.workPeriodId || row.WorkPeriodId || row.WorkperiodId || row.workperiodId;
        const payoutAttemptResult = payoutAttemptResultsByWorkPeriodId[String(workPeriodId)] || null;
        const canRetryFailedTransfers = !!(payoutAttemptResult?.canRetryFailedTransfers && payoutAttemptResult?.failedTransfersAvailable > 0);
        const hasHeldTransfers = !!(payoutAttemptResult?.heldCount > 0 || payoutAttemptResult?.hasHeldTransfers);
        const isRetryingFailedTransfers = String(retryingFailedWorkPeriodId) === String(workPeriodId);

        // Show bulk retry button when failed transfers can be retried (takes precedence)
        if (canRetryFailedTransfers) {
            return (
                <button
                    className={s.retryBtn}
                    onClick={(e) => { e.stopPropagation(); this.handleRetryFailedTransfers(row); }}
                    disabled={isTriggering || isRetryingFailedTransfers}
                >
                    {isRetryingFailedTransfers ? <Loader size={14} /> : <><i className="zmdi zmdi-refresh"></i> Retry Failed</>}
                </button>
            );
        }

        if (hasHeldTransfers) {
            return <span className={s.actionNa} title="Payout is waiting for funds to clear before it can be retried automatically.">Waiting for Funds to Clear</span>;
        }

        // Show single-transfer retry button for individually failed transfers
        if (status === 'failed' && row.TransferId) {
            return (
                <button 
                    className={s.retryBtn}
                    onClick={(e) => { e.stopPropagation(); this.handleRetry(row.TransferId, row.id); }}
                    disabled={isRetrying}
                >
                    {isRetrying ? <Loader size={14} /> : <><i className="zmdi zmdi-refresh"></i> Retry</>}
                </button>
            );
        }

        // Show trigger payout button for unpaid items with amounts
        if ((status === 'none' || !status) && hasAmount && !this.isTipPayoutDisabled()) {
            return (
                <button 
                    className={s.triggerBtn}
                    onClick={() => this.handleTriggerPayout(row)}
                    disabled={isTriggering}
                >
                    {isTriggering ? <Loader size={14} /> : <><i className="zmdi zmdi-money"></i> Pay</>}
                </button>
            );
        }

        return <span className={s.actionNa}>-</span>;
    }

    isTipPayoutDisabled = () => {
        return Number(this.props.tipPayoutRoutingMode) === 3;
    }

    handleRetry = (transferId, saleId) => {
        this.props.dispatch(retryPayoutRequest(transferId, saleId));
    }

    getEmployeeDisplayName = () => {
        const employee = this.props.selectedEmployee;
        return employee?.Username || employee?.username || 'Employee';
    }

    handleTriggerPayout = (row) => {
        const userId = parseInt(this.props.match.params.userId);
        const wpId = row.workPeriodId || row.WorkPeriodId || row.WorkperiodId || row.workperiodId;
        if (!wpId) return;
        this.props.dispatch(
            triggerPayoutRequest(userId, wpId, row.id, this.getEmployeeDisplayName()),
        );
    }

    handleRetryFailedTransfers = (row) => {
        const userId = parseInt(this.props.match.params.userId);
        const wpId = row.workPeriodId || row.WorkPeriodId || row.WorkperiodId || row.workperiodId;
        if (!wpId) return;
        if (!window.confirm('Retry all failed transfers for this work period? This will re-attempt real payments.')) return;
        this.props.dispatch(
            retryFailedTransfersRequest(userId, wpId, row.id, this.getEmployeeDisplayName()),
        );
    }

    salesFormatter = (cell, row) => {
        const amounts = salesAmounts(row);
        const nets = reportingNets(row);
        return (
            <div className={s.salesCell}>
                <span className={s.salesItem}>
                    <small>{this.props.t('report.cash')}:</small> {this.formatCurrency(amounts.cash)}
                </span>
                <span className={s.salesItem}>
                    <small>{this.props.t('report.card')}:</small> {this.formatCurrency(amounts.card)}
                </span>
                <span className={s.salesItem}>
                    <small>{this.props.t('report.member')}:</small> {this.formatCurrency(amounts.member)}
                </span>
                {nets.merchRefunds > 0 ? (
                    <span className={s.salesItem}>
                        <small>{this.props.t('report.salesAfterRefunds')}:</small> {this.formatCurrency(nets.netSalesMerch)}
                        {nets.merchRefundEstimated ? <small title={this.props.t('report.refundEstimateFootnote')}> *</small> : null}
                    </span>
                ) : null}
            </div>
        );
    }

    renderSizePerPageDropDown = (props) => {
        const limits = [];
        props.sizePerPageList.forEach((limit) => {
            limits.push(
                <DropdownItem 
                    key={limit}
                    onClick={() => props.changeSizePerPage(limit)}
                >
                    {limit}
                </DropdownItem>
            );
        });

        return (
            <Dropdown isOpen={props.open} toggle={props.toggleDropDown}>
                <DropdownToggle color="default" caret>
                    {props.currSizePerPage}
                </DropdownToggle>
                <DropdownMenu>
                    {limits}
                </DropdownMenu>
            </Dropdown>
        );
    };

    getBackPath = () => {
        return this.props.backPath || '/admin/users';
    }

    getBasePath = () => {
        return this.props.basePath || '/admin/users/sales';
    }

    goBack = (e) => {
        e.preventDefault();
        this.props.history.push(this.getBackPath());
    }

    getFilteredSalesView(employeeSales, employeeSalesSummary, startDate, endDate) {
        const deduped = Object.values(
            employeeSales.reduce((acc, row) => {
                const periodKey = this.getPeriodRowKey(row);
                if (!periodKey) {
                    return acc;
                }

                if (!acc[periodKey]) {
                    acc[periodKey] = { ...row };
                    return acc;
                }

                const existingHours = parseFloat(acc[periodKey].Hours) || 0;
                const newHours = parseFloat(row.Hours) || 0;
                acc[periodKey].Hours = Math.max(existingHours, newHours);

                if (row.ClockIn && (!acc[periodKey].ClockIn || new Date(row.ClockIn) < new Date(acc[periodKey].ClockIn))) {
                    acc[periodKey].ClockIn = row.ClockIn;
                }
                // Any open segment for the period means Still Active — do not keep an intermediate End.
                if (this.isClockOutEmpty(acc[periodKey].ClockOut) || this.isClockOutEmpty(row.ClockOut)) {
                    acc[periodKey].ClockOut = null;
                } else if (new Date(row.ClockOut) > new Date(acc[periodKey].ClockOut)) {
                    acc[periodKey].ClockOut = row.ClockOut;
                }

                return acc;
            }, {}),
        );

        const filteredRows = this.filterRowsByDateRange(deduped, startDate, endDate)
            .sort((a, b) => {
                const aDate = this.getDisplayWorkperiodDate(a);
                const bDate = this.getDisplayWorkperiodDate(b);
                if (!aDate || !bDate) return 0;
                return bDate - aDate;
            });

        const totals = filteredRows.reduce((acc, row) => {
            const tips = parseFloat(row.Tips) || 0;
            const gratuity = parseFloat(row.Gratuity) || 0;
            const { paid, owed } = this.getRowTipPaidOwed(row);

            const amounts = salesAmounts(row);
            const nets = reportingNets(row);
            return {
                cash: acc.cash + amounts.cash,
                card: acc.card + amounts.card,
                member: acc.member + amounts.member,
                fees: acc.fees + amounts.fees,
                tips: acc.tips + tips,
                gratuity: acc.gratuity + gratuity,
                hours: acc.hours + (parseFloat(row.Hours) || 0),
                owed: acc.owed + owed,
                paid: acc.paid + paid,
                yourShare: acc.yourShare + this.rowYourShare(row),
                includePooled: acc.includePooled || this.isPooledPeriod(row),
                salesBeforeRefunds: acc.salesBeforeRefunds + nets.salesBeforeRefunds,
                discounts: acc.discounts + nets.discounts,
                comps: acc.comps + nets.comps,
                merchRefunds: acc.merchRefunds + nets.merchRefunds,
                netSalesMerch: acc.netSalesMerch + nets.netSalesMerch,
                customerRefunds: acc.customerRefunds + nets.customerRefunds,
                tipRefundedDisplay: acc.tipRefundedDisplay + nets.tipRefundedDisplay,
                netTipsDisplay: acc.netTipsDisplay + nets.netTipsDisplay,
                gratuityRefundedDisplay: acc.gratuityRefundedDisplay + nets.gratuityRefundedDisplay,
                netGratuityDisplay: acc.netGratuityDisplay + nets.netGratuityDisplay,
                feeRefundedDisplay: acc.feeRefundedDisplay + nets.feeRefundedDisplay,
                netFeeDisplay: acc.netFeeDisplay + nets.netFeeDisplay,
                merchRefundEstimated: acc.merchRefundEstimated || nets.merchRefundEstimated,
                tipGratRefundEstimated: acc.tipGratRefundEstimated || nets.tipGratRefundEstimated,
            };
        }, {
            cash: 0, card: 0, member: 0, fees: 0, tips: 0, gratuity: 0, hours: 0, owed: 0, paid: 0, yourShare: 0, includePooled: false,
            salesBeforeRefunds: 0, discounts: 0, comps: 0, merchRefunds: 0, netSalesMerch: 0, customerRefunds: 0,
            tipRefundedDisplay: 0, netTipsDisplay: 0, gratuityRefundedDisplay: 0, netGratuityDisplay: 0,
            feeRefundedDisplay: 0, netFeeDisplay: 0, merchRefundEstimated: false, tipGratRefundEstimated: false,
        });

        const periodSummary = this.resolvePeriodSummary(employeeSalesSummary, filteredRows, totals);

        return { filteredRows, periodSummary };
    }

    saleTimeStatusLabel = (row) => {
        const status = row.TipPayoutStatus || 'none';
        return formatTipPayoutStatusLabel(status, this.resolveRowManualPayReason(row));
    }

    buildPdfExportRows(filteredRows) {
        const pooledRows = [];
        const saleTimeRows = [];
        filteredRows.forEach((row) => {
            const date = this.formatWorkperiodDate(row.WorkperiodDate || row.workperiodDate, row);
            const amounts = salesAmounts(row);
            const cash = this.formatCurrency(amounts.cash);
            const card = this.formatCurrency(amounts.card);
            const member = this.formatCurrency(amounts.member);
            const fees = this.formatCurrency(amounts.fees);
            const hours = this.formatHours(row.Hours);
            if (this.isPooledPeriod(row)) {
                pooledRows.push({
                    date,
                    cash,
                    card,
                    member,
                    fees,
                    pool: this.formatPoolAmount(row),
                    share: this.formatShareAmount(row),
                    hours,
                    status: this.pooledStatusLabel(row),
                    clockIn: row.ClockIn ? this.formatTime(row.ClockIn) : '',
                    clockOut: this.formatClockOutLabel(row.ClockOut),
                });
                return;
            }
            const tips = parseFloat(row.Tips) || 0;
            const gratuity = parseFloat(row.Gratuity) || 0;
            saleTimeRows.push({
                date,
                cash,
                card,
                member,
                fees,
                tips: this.formatCurrency(tips),
                gratuity: this.formatCurrency(gratuity),
                hours,
                earnings: this.formatCurrency(tips + gratuity),
                status: this.saleTimeStatusLabel(row),
            });
        });
        return { pooledRows, saleTimeRows };
    }

    buildSpreadsheetExportRows(filteredRows) {
        const pooledRows = [];
        const saleTimeRows = [];
        filteredRows.forEach((row) => {
            const date = this.formatWorkperiodDate(row.WorkperiodDate || row.workperiodDate, row);
            const amounts = salesAmounts(row);
            const cash = amounts.cash;
            const card = amounts.card;
            const member = amounts.member;
            const fees = amounts.fees;
            const hours = parseFloat(row.Hours) || 0;
            const clockIn = row.ClockIn ? this.formatTime(row.ClockIn) : '';
            const clockOut = this.formatClockOutLabel(row.ClockOut);
            if (this.isPooledPeriod(row)) {
                pooledRows.push({
                    date,
                    cash,
                    card,
                    member,
                    fees,
                    pool: parseFloat(row.PeriodTipPool ?? row.periodTipPool) || 0,
                    share: this.readFlag(row, 'InPooledShare', 'inPooledShare') ? this.rowYourShare(row) : '—',
                    hours,
                    status: this.pooledStatusLabel(row),
                    clockIn,
                    clockOut,
                });
                return;
            }
            const tips = parseFloat(row.Tips) || 0;
            const gratuity = parseFloat(row.Gratuity) || 0;
            saleTimeRows.push({
                date,
                cash,
                card,
                member,
                fees,
                tips,
                gratuity,
                hours,
                earnings: tips + gratuity,
                status: this.saleTimeStatusLabel(row),
                clockIn,
                clockOut,
            });
        });
        return { pooledRows, saleTimeRows };
    }

    formatPoolAmount = (row) => this.formatCurrency(parseFloat(row.PeriodTipPool ?? row.periodTipPool) || 0)

    formatShareAmount = (row) => {
        if (!this.readFlag(row, 'InPooledShare', 'inPooledShare')) return '—';
        return this.formatCurrency(this.rowYourShare(row));
    }

    getExportContext() {
        if (this.state.exportLoading || this.state.sendReportLoading || this.props.employeeSalesLoading) {
            return null;
        }

        const { employeeSales, employeeSalesSummary, selectedEmployee, merchant } = this.props;
        const { startDate, endDate } = this.state;
        const { filteredRows, periodSummary } = this.getFilteredSalesView(
            employeeSales,
            employeeSalesSummary,
            startDate,
            endDate,
        );

        if (!filteredRows.length) {
            toast.info('No data to export for this date range.');
            return null;
        }

        return {
            employeeName: selectedEmployee?.Username || selectedEmployee?.username || 'Employee',
            storeName: merchant?.StoreName || '',
            startDate,
            endDate,
            periodLabel: this.formatPeriodRange(startDate, endDate),
            summary: periodSummary,
            pdfRows: this.buildPdfExportRows(filteredRows),
            spreadsheetRows: this.buildSpreadsheetExportRows(filteredRows),
        };
    }

    toggleExportMenu = () => {
        this.setState((prev) => ({ exportMenuOpen: !prev.exportMenuOpen }));
    }

    handleExport = async (format) => {
        const context = this.getExportContext();
        if (!context) {
            return;
        }

        this.setState({ exportLoading: true, exportMenuOpen: false });

        try {
            const payload = {
                employeeName: context.employeeName,
                storeName: context.storeName,
                startDate: context.startDate,
                endDate: context.endDate,
                periodLabel: context.periodLabel,
                summary: context.summary,
            };

            if (format === 'pdf') {
                const { exportEmployeeSalesPdf } = await import('../../../utils/employeeSalesPdfExport');
                exportEmployeeSalesPdf({
                    ...payload,
                    rows: context.pdfRows,
                    t: this.props.t,
                });
            } else {
                const {
                    exportEmployeeSalesCsv,
                    exportEmployeeSalesExcel,
                } = await import('../../../utils/employeeSalesSpreadsheetExport');
                const spreadsheetPayload = {
                    ...payload,
                    detailRows: context.spreadsheetRows,
                    t: this.props.t,
                };

                if (format === 'csv') {
                    exportEmployeeSalesCsv(spreadsheetPayload);
                } else {
                    exportEmployeeSalesExcel(spreadsheetPayload);
                }
            }

            const formatLabel = format === 'xlsx' ? 'Excel' : format.toUpperCase();
            toast.success(`Sales report downloaded (${formatLabel}).`);
        } catch (error) {
            console.error('Failed to export employee sales report:', error);
            toast.error('Failed to export sales report.');
        } finally {
            this.setState({ exportLoading: false });
        }
    }

    buildSendReportPayload = async (context) => {
        const { buildEmployeeSalesPdfBase64 } = await import('../../../utils/employeeSalesPdfExport');
        const { fileName, pdfBase64 } = buildEmployeeSalesPdfBase64({
            employeeName: context.employeeName,
            storeName: context.storeName,
            startDate: context.startDate,
            endDate: context.endDate,
            summary: context.summary,
            rows: context.pdfRows,
            t: this.props.t,
        });

        return {
            username: context.employeeName,
            storeName: context.storeName,
            startDate: context.startDate,
            endDate: context.endDate,
            periodLabel: context.periodLabel,
            fileName,
            pdfBase64,
        };
    }

    handleSendReportEmail = async () => {
        const context = this.getExportContext();
        const employee = this.props.selectedEmployee;
        const email = employee?.Email || employee?.email;

        if (!context) {
            return;
        }

        if (!email) {
            toast.info('Add an email address on the employee profile to send reports.');
            return;
        }

        this.setState({ sendReportLoading: 'email' });

        try {
            const payload = await this.buildSendReportPayload(context);
            await this.props.dispatch(sendEmployeeSalesReportEmailRequest(
                parseInt(this.props.match.params.userId, 10),
                { ...payload, email },
            ));
            toast.success(`Sales report sent to ${email}`);
        } catch (error) {
            console.error('Failed to email employee sales report:', error);
            toast.error(error.message || 'Failed to send sales report.');
        } finally {
            this.setState({ sendReportLoading: null });
        }
    }

    handleSendReportSms = async () => {
        const context = this.getExportContext();
        const employee = this.props.selectedEmployee;
        const phone = employee?.Telephone || employee?.telephone;

        if (!context) {
            return;
        }

        if (!phone) {
            toast.info('Add a phone number on the employee profile to send reports.');
            return;
        }

        this.setState({ sendReportLoading: 'sms' });

        try {
            const payload = await this.buildSendReportPayload(context);
            await this.props.dispatch(sendEmployeeSalesReportSmsRequest(
                parseInt(this.props.match.params.userId, 10),
                { ...payload, phone },
            ));
            toast.success(`Sales report sent to ${phone}`);
        } catch (error) {
            console.error('Failed to text employee sales report:', error);
            toast.error(error.message || 'Failed to send sales report.');
        } finally {
            this.setState({ sendReportLoading: null });
        }
    }

    handleRowClick = (row) => {
        const userId = this.props.match.params.userId;
        const wpId = row.workPeriodId || row.WorkPeriodId || row.WorkperiodId || row.workperiodId;
        if (!wpId) return;
        this.props.history.push({
            pathname: `${this.getBasePath()}/${userId}/workperiod/${wpId}`,
            state: {
                workperiod: {
                    ...row,
                    WorkperiodId: wpId,
                    Tips: row.Tips ?? row.tips,
                    Gratuity: row.Gratuity ?? row.gratuity,
                    CashSales: row.CashSales ?? row.cashSales,
                    CardSales: row.CardSales ?? row.cardSales,
                    MemberSales: row.MemberSales ?? row.memberSales ?? 0,
                    Fees: row.Fees ?? row.fees ?? row.Fee ?? row.fee,
                    // Prefer API flag. Default true: employee CardSales is merch-only (Fees separate).
                    CardSalesExcludeFees: (() => {
                        const raw = row.CardSalesExcludeFees ?? row.cardSalesExcludeFees;
                        if (raw === true || raw === 1 || raw === 'true' || raw === 'True') return true;
                        if (raw === false || raw === 0 || raw === 'false' || raw === 'False') return false;
                        return true;
                    })(),
                    Hours: row.Hours ?? row.hours,
                    WorkperiodDate: row.WorkperiodDate ?? row.workperiodDate,
                    ClockIn: row.ClockIn ?? row.clockIn,
                    ClockOut: row.ClockOut ?? row.clockOut,
                    TipPayoutStatus: row.TipPayoutStatus ?? row.tipPayoutStatus,
                    TransferId: row.TransferId ?? row.transferId,
                },
                employee: this.props.selectedEmployee
            }
        });
    }

    renderSalesTables = (filteredRows, t) => {
        const pooledRows = filteredRows.filter((row) => this.isPooledPeriod(row));
        const saleRows = filteredRows.filter((row) => !this.isPooledPeriod(row));
        const showSaleHeading = pooledRows.length > 0 && saleRows.length > 0;
        const saleColumns = [
            { key: 'WorkperiodDate', header: t('report.date'), render: (cell, row) => <strong>{this.formatWorkperiodDate(cell, row)}</strong> },
            { key: 'CashSales', header: t('report.sales'), render: (cell, row) => this.salesFormatter(cell, row) },
            { key: 'Tips', header: t('report.tips'), render: (cell) => this.formatCurrency(cell) },
            { key: 'Gratuity', header: t('report.gratuity'), render: (cell) => this.formatCurrency(cell) },
            { key: 'Hours', header: t('report.hours'), render: (cell) => this.formatHours(cell) },
            { key: 'id', header: t('report.earnings'), render: (cell, row) => this.earningsFormatter(cell, row) },
            { key: 'ClockIn', header: t('report.clockIn'), render: (cell) => this.formatTime(cell), hideMobile: true },
            { key: 'ClockOut', header: t('report.clockOut'), render: (cell) => this.formatClockOutDisplay(cell), hideMobile: true },
            { key: 'TipPayoutStatus', header: '', render: (cell, row) => this.statusFormatter(cell, row), className: 'col-status' },
            { key: 'id', header: t('common.action'), render: (cell, row) => this.actionFormatter(cell, row), hideMobile: true },
        ];
        const pooledColumns = [
            { key: 'WorkperiodDate', header: t('report.date'), render: (cell, row) => <strong>{this.formatWorkperiodDate(cell, row)}</strong> },
            { key: 'CashSales', header: t('report.sales'), render: (cell, row) => this.salesFormatter(cell, row) },
            { key: 'PeriodTipPool', header: t('report.totalTipsAndGrat'), render: (cell, row) => this.formatPoolAmount(row) },
            { key: 'YourShare', header: t('report.yourShare'), render: (cell, row) => this.formatShareAmount(row) },
            { key: 'Hours', header: t('report.hours'), render: (cell) => this.formatHours(cell) },
            { key: 'ClockIn', header: t('report.clockIn'), render: (cell) => this.formatTime(cell), hideMobile: true },
            { key: 'ClockOut', header: t('report.clockOut'), render: (cell) => this.formatClockOutDisplay(cell), hideMobile: true },
            { key: 'TipPayoutStatus', header: t('report.status'), render: (cell, row) => this.statusFormatter(cell, row), className: 'col-status' },
            { key: 'id', header: t('common.action'), render: (cell, row) => this.actionFormatter(cell, row), hideMobile: true },
        ];

        if (pooledRows.length === 0 && saleRows.length === 0) {
            return (
                <Widget>
                    <div className="order_list">
                        <NativeTable
                            data={[]}
                            onRowClick={this.handleRowClick}
                            columns={saleColumns}
                        />
                    </div>
                </Widget>
            );
        }

        return (
            <>
                {pooledRows.length > 0 ? (
                    <Widget>
                        <h2 className={s.reportSectionTitle}>{t('report.pooledWorkPeriods')}</h2>
                        <p className={s.reportExplainer}>{t('report.totalTipsGratExplainer')}</p>
                        <div className="order_list">
                            <NativeTable
                                data={pooledRows}
                                onRowClick={this.handleRowClick}
                                columns={pooledColumns}
                            />
                        </div>
                    </Widget>
                ) : null}
                {saleRows.length > 0 ? (
                    <Widget>
                        {showSaleHeading ? (
                            <h2 className={s.reportSectionTitle}>{t('report.saleTimeWorkPeriods')}</h2>
                        ) : null}
                        <div className="order_list">
                            <NativeTable
                                data={saleRows}
                                onRowClick={this.handleRowClick}
                                columns={saleColumns}
                            />
                        </div>
                    </Widget>
                ) : null}
            </>
        );
    }

    render() {
        const { employeeSales, employeeSalesLoading, employeeSalesSummary, selectedEmployee, t } = this.props;
        const { startDate, endDate, exportLoading, exportMenuOpen, sendReportLoading } = this.state;
        const { filteredRows, periodSummary } = this.getFilteredSalesView(
            employeeSales,
            employeeSalesSummary,
            startDate,
            endDate,
        );
        const canExport = !employeeSalesLoading && filteredRows.length > 0;
        const employeeEmail = selectedEmployee?.Email || selectedEmployee?.email;
        const employeePhone = selectedEmployee?.Telephone || selectedEmployee?.telephone;
        const reportActionBusy = exportLoading || !!sendReportLoading;

        const options = {
            sizePerPage: 10,
            paginationSize: 3,
            sizePerPageDropDown: this.renderSizePerPageDropDown,
            onRowClick: this.handleRowClick,
        };

        return (
            <div className="page-wrapper">
                <div className="page-content">
                    <div className={s.header}>
                        <div className={s.headerLeft}>
                            <a className="back-link" href="#" onClick={this.goBack}>
                                <i className="zmdi zmdi-arrow-left"></i>
                            </a>
                            <div>
                                <h1 className={s.title}>
                                    {t('report.employeeSalesHeading', { name: selectedEmployee?.Username || t('report.employee') })}
                                </h1>
                                <p className={s.subtitle}>
                                    {t('report.employeeSalesSubtitle')}
                                </p>
                            </div>
                        </div>
                        <div className={s.headerActions}>
                            <button
                                type="button"
                                className={s.sendReportBtn}
                                onClick={this.handleSendReportEmail}
                                disabled={!canExport || !employeeEmail || reportActionBusy}
                                title={employeeEmail ? t('report.emailPdf', { email: employeeEmail }) : t('report.noEmail')}
                                aria-label={t('report.emailPdf', { email: employeeEmail || '' })}
                            >
                                {sendReportLoading === 'email' ? (
                                    <Loader size={16} />
                                ) : (
                                    <i className="zmdi zmdi-email" aria-hidden="true" />
                                )}
                            </button>
                            <button
                                type="button"
                                className={s.sendReportBtn}
                                onClick={this.handleSendReportSms}
                                disabled={!canExport || !employeePhone || reportActionBusy}
                                title={employeePhone ? t('report.textPdf', { phone: employeePhone }) : t('report.noPhone')}
                                aria-label={t('report.textPdf', { phone: employeePhone || '' })}
                            >
                                {sendReportLoading === 'sms' ? (
                                    <Loader size={16} />
                                ) : (
                                    <i className="zmdi zmdi-smartphone" aria-hidden="true" />
                                )}
                            </button>
                        <Dropdown
                            isOpen={exportMenuOpen}
                            toggle={this.toggleExportMenu}
                            className={s.exportMenu}
                        >
                            <DropdownToggle
                                tag="button"
                                type="button"
                                className={s.exportBtn}
                                disabled={!canExport || reportActionBusy}
                                title={t('report.exportTitle')}
                                aria-label={t('report.exportTitle')}
                            >
                                {exportLoading ? (
                                    <>
                                        <Loader size={16} />
                                        {t('report.exporting')}
                                    </>
                                ) : (
                                    <>
                                        <i className="zmdi zmdi-download" aria-hidden="true" />
                                        {t('report.export')}
                                        <i className={`zmdi zmdi-chevron-down ${s.exportCaret}`} aria-hidden="true" />
                                    </>
                                )}
                            </DropdownToggle>
                            <DropdownMenu right className={s.exportMenuDropdown}>
                                <DropdownItem onClick={() => this.handleExport('pdf')}>
                                    <i className="zmdi zmdi-file" aria-hidden="true" />
                                    {t('report.pdf')}
                                </DropdownItem>
                                <DropdownItem onClick={() => this.handleExport('csv')}>
                                    <i className="zmdi zmdi-file-text" aria-hidden="true" />
                                    {t('report.csv')}
                                </DropdownItem>
                                <DropdownItem onClick={() => this.handleExport('xlsx')}>
                                    <i className="zmdi zmdi-grid" aria-hidden="true" />
                                    {t('report.excel')}
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                        </div>
                    </div>

                    {/* Date Filters */}
                    <div className={s.filters}>
                        <div className={s.filterGroup}>
                            <label>{t('workperiods.startDate')}</label>
                            <input 
                                type="date" 
                                className="form-control"
                                value={startDate}
                                onChange={this.handleStartDateChange}
                            />
                        </div>
                        <div className={s.filterGroup}>
                            <label>{t('workperiods.endDate')}</label>
                            <input 
                                type="date" 
                                className="form-control"
                                value={endDate}
                                onChange={this.handleEndDateChange}
                            />
                        </div>
                    </div>

                    {!employeeSalesLoading ? this.renderPeriodSummary(periodSummary, startDate, endDate) : null}

                    {employeeSalesLoading ? (
                        <div className={s.loaderContainer}>
                            <Loader size={60} />
                        </div>
                    ) : (
                        this.renderSalesTables(filteredRows, t)
                    )}
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        employeeSales: state.users.employeeSales || [],
        employeeSalesLoading: state.users.employeeSalesLoading || false,
        employeeSalesSummary: state.users.employeeSalesSummary || null,
        selectedEmployee: state.users.selectedEmployee || null,
        payoutStatus: state.users.payoutStatus || null,
        payoutStatusLoading: state.users.payoutStatusLoading || false,
        payoutHistory: state.users.payoutHistory || null,
        payoutHistoryLoading: state.users.payoutHistoryLoading || false,
        retryingTransferId: state.users.retryingTransferId || null,
        triggeringSaleId: state.users.triggeringSaleId || null,
        retryingFailedWorkPeriodId: state.users.retryingFailedWorkPeriodId || null,
        payoutAttemptResultsByWorkPeriodId: state.users.payoutAttemptResultsByWorkPeriodId || {},
        tipPayoutRoutingMode: state.settings.storeDetails?.TipPayoutRoutingMode || state.auth.merchant?.TipPayoutRoutingMode || 3,
        storeSettingsMerchantId: state.settings.storeDetails?.MerchantId || null,
        merchant: state.auth.merchant || null,
    };
}

export default withRouter(connect(mapStateToProps)(withTranslation(EmployeeSales)));
